import {
  createRemoteCollection,
  type RemoteCollection,
  type RemoteCollectionHandle,
  type RemoteCollectionOptions,
  type RemoteCollectionPagingOptions,
  type RemoteCollectionPageRequest,
  type RemoteCollectionPageResult,
  type RemoteCollectionSorting,
  type RemoteCollectionQuery,
  type RemoteCollectionQueryConstraint,
} from "../data/remote-collection"
import { normalizeDataDependencies, type DataDependency, type DataDependencyInput } from "../data/data-invalidation"
import type {
  AnyGraphQLDocument,
  GraphQLDocument,
  GraphQLDocumentData,
  GraphQLDocumentVariables,
} from "./graphql-document"
import {
  GraphQLQueryInstance,
  type GraphQLQueryExecutionOptions,
  type GraphQLQueryExecutionTarget,
  type GraphQLQueryCodec,
} from "./graphql-query"

export interface GraphQLConnectionPageInfo {
  startCursor?: string | null
  endCursor?: string | null
  hasNextPage?: boolean
  hasPreviousPage?: boolean
}

export interface GraphQLConnection<T> {
  nodes?: readonly T[] | null
  totalCount?: number | null
  pageInfo?: GraphQLConnectionPageInfo | null
}

/** Paging values matching FlorisoftCursorPagingHandler semantics. */
export interface ConnectionPagingVariables<TSortField extends string = string, TBackendField extends string = string> {
  pageSize: number
  after: string | null
  before: string | null
  /** Map this to the Connection field's `last:` argument; Florisoft treats it as a skip offset. */
  skip: number
  /** Useful with `totalCount @include(if: $includeTotalCount)` to avoid repeated COUNT queries. */
  includeTotalCount: boolean
  /** Applied remote order. The feature maps ids to its backend schema. */
  sorting?: RemoteCollectionSorting<TSortField>
  /** Backend order derived from the definition's supported field mapping. */
  order?: Partial<Record<TBackendField, "ASC" | "DESC">>[]
}

/** Conventional variable names used by Florisoft Connection operations. */
export interface GraphQLConnectionVariables<TBackendField extends string = string> {
  first: number
  after: string | null
  before: string | null
  skip: number
  includeTotalCount: boolean
  order?: Partial<Record<TBackendField, "ASC" | "DESC">>[]
}

type ConnectionMapperPagingVariables<
  TSortField extends string,
  TBackendField extends string,
> = ConnectionPagingVariables<TSortField, TBackendField> & { order: Partial<Record<TBackendField, "ASC" | "DESC">>[] }

/** Mapper paging inferred from a definition's UI-id to backend-field sorting map. */
export type ConnectionPagingVariablesFor<TSorting extends Readonly<Record<string, string>>> =
  ConnectionMapperPagingVariables<Extract<keyof TSorting, string>, TSorting[keyof TSorting]>

/** Hook-owned collection type inferred from a connection query, for component props. */
export type GraphQLCollectionOf<TDefinition> = TDefinition extends {
  readonly selectConnection: (data: never) => GraphQLConnection<infer TItem>
  readonly sorting: Readonly<Record<infer TSortField, string>>
  create: (...args: infer TArguments) => unknown
}
  ? RemoteCollectionHandle<
      TItem,
      TArguments extends [infer TParameters] ? TParameters : void,
      Extract<TSortField, string>
    >
  : never

export type GraphQLCollectionOptions<
  TItem,
  TParameters = void,
  TSortField extends string = string,
> = RemoteCollectionPagingOptions &
  Pick<
    RemoteCollectionOptions<TItem, TParameters, TSortField>,
    "getKey" | "sortableFields" | "initialSorting" | "selectionOnQueryChange" | "maxSortFields" | "complete"
  > &
  ([TParameters] extends [void]
    ? { initialQuery?: never }
    : { initialQuery: TParameters & RemoteCollectionQuery<TParameters> })

/** Headless GraphQL collection. Its owner disposes it when finished. */
export function createGraphQLCollection<
  TData,
  TItem,
  TParameters,
  TVariables extends object,
  TSortField extends string,
  TBackendField extends string,
>(
  definition: GraphQLConnectionQueryDefinition<TData, TItem, TParameters, TVariables, TSortField, TBackendField>,
  target: GraphQLQueryExecutionTarget,
  options: GraphQLCollectionOptions<NoInfer<TItem>, NoInfer<TParameters>, NoInfer<TSortField>>
): RemoteCollection<TItem, TParameters, TSortField> {
  const create = definition.create.bind(definition) as (
    parameters?: TParameters
  ) => GraphQLConnectionQueryInstance<TData, TItem, TVariables, TSortField, TBackendField>
  const hasParameters = Object.prototype.hasOwnProperty.call(options, "initialQuery")
  const supported = Object.keys(definition.sorting) as TSortField[]
  if (options.sortableFields?.some((field) => !supported.includes(field)))
    throw new Error("GraphQL collection sortableFields must be a subset of definition.sorting.")
  if (options.maxSortFields !== undefined && options.maxSortFields > definition.maxSortFields) {
    throw new Error("GraphQL collection maxSortFields cannot exceed its definition's limit.")
  }
  return createRemoteCollection<TItem, TParameters, TSortField>({
    ...options,
    sortableFields: options.sortableFields ?? supported,
    maxSortFields: options.maxSortFields ?? definition.maxSortFields,
    initialQuery: (options as { initialQuery?: TParameters }).initialQuery as TParameters &
      RemoteCollectionQuery<TParameters>,
    fetchPage: (request) =>
      (hasParameters ? create(request.query as TParameters) : create()).fetchPage(target, request),
  } as RemoteCollectionOptions<TItem, TParameters, TSortField> & RemoteCollectionQueryConstraint<NoInfer<TParameters>>)
}

/** Translate ordered field ids to GraphQL sort inputs; preserves multi-sort precedence. */
export function graphQLOrder<TSortField extends string, TBackendField extends string = TSortField>(
  sorting: RemoteCollectionSorting<TSortField> | undefined,
  fieldMap?: Readonly<Record<TSortField, TBackendField>>
): Partial<Record<TBackendField, "ASC" | "DESC">>[] {
  return (sorting ?? []).map(({ id, desc }) => {
    const field = fieldMap ? fieldMap[id] : (id as unknown as TBackendField)
    if (!field) throw new Error(`No GraphQL sort mapping for "${id}".`)
    return { [field]: desc ? "DESC" : "ASC" } as Partial<Record<TBackendField, "ASC" | "DESC">>
  })
}

export function graphQLConnectionVariables<TSortField extends string, TBackendField extends string>(
  paging: ConnectionPagingVariables<TSortField, TBackendField>
): GraphQLConnectionVariables<TBackendField> {
  return {
    first: paging.pageSize,
    after: paging.after,
    before: paging.before,
    skip: paging.skip,
    includeTotalCount: paging.includeTotalCount,
    ...(paging.order ? { order: paging.order } : {}),
  }
}

type ConnectionQueryCreateArguments<TParameters> = [TParameters] extends [void] ? [] : [parameters: TParameters]

type ConnectionVariablesBuilder<
  TParameters,
  TVariables extends object,
  TSortField extends string = string,
  TBackendField extends string = string,
> = [TParameters] extends [void]
  ? (paging: ConnectionMapperPagingVariables<TSortField, TBackendField>) => TVariables
  : (
      parameters: Readonly<TParameters>,
      paging: ConnectionMapperPagingVariables<TSortField, TBackendField>
    ) => TVariables

type ConnectionItem<TConnection extends GraphQLConnection<unknown>> =
  TConnection extends GraphQLConnection<infer TItem> ? TItem : never

export interface GraphQLConnectionQueryDefinitionOptions<
  TDocument extends AnyGraphQLDocument,
  TConnection extends GraphQLConnection<unknown>,
  TSorting extends Readonly<Record<string, string>> = Readonly<Record<string, string>>,
> {
  endpoint: string
  document: TDocument
  codec?: GraphQLQueryCodec
  variables: (paging: ConnectionPagingVariablesFor<TSorting>) => GraphQLDocumentVariables<TDocument>
  selectConnection: (data: GraphQLDocumentData<TDocument>) => TConnection
  /** Current Florisoft cursor handler reports page flags conservatively; disabled by default. */
  usePageInfoFlags?: boolean
  /** Supported ids map to backend field names. Capabilities derive from this definition. */
  sorting?: TSorting
  /** Stable backend order used whenever user sorting is empty. */
  defaultSorting?: RemoteCollectionSorting<NoInfer<Extract<keyof TSorting, string>>>
  maxSortFields?: number
  consistency?: GraphQLConnectionQueryConsistency<void, ConnectionItem<TConnection>>
}

export interface MappedGraphQLConnectionQueryDefinitionOptions<
  TDocument extends AnyGraphQLDocument,
  TParameters,
  TConnection extends GraphQLConnection<unknown>,
  TSorting extends Readonly<Record<string, string>> = Readonly<Record<string, string>>,
> extends Omit<GraphQLConnectionQueryDefinitionOptions<TDocument, TConnection, TSorting>, "variables" | "consistency"> {
  variables: (
    parameters: Readonly<TParameters>,
    paging: ConnectionPagingVariablesFor<TSorting>
  ) => GraphQLDocumentVariables<TDocument>
  consistency?: GraphQLConnectionQueryConsistency<TParameters, ConnectionItem<TConnection>>
}

export interface GraphQLConnectionQueryConsistency<TParameters, TItem> {
  /** Dependencies that make this collection stale. */
  readonly dependsOn: DataDependencyInput | ((parameters: Readonly<TParameters>) => DataDependencyInput)
  /** Optional item-level dependencies used for targeted loaded-page refreshes. */
  readonly itemDependsOn?: (item: Readonly<TItem>) => DataDependencyInput
}

interface ResolvedGraphQLConnectionConsistency<TItem> {
  readonly collection: readonly DataDependency[]
  readonly item?: (item: Readonly<TItem>) => readonly DataDependency[]
}

export class GraphQLConnectionQueryInstance<
  TData,
  TItem,
  TVariables extends object,
  TSortField extends string = string,
  TBackendField extends string = string,
> {
  constructor(
    private readonly endpoint: string,
    private readonly document: GraphQLDocument<TData, TVariables>,
    private readonly selectConnection: (data: TData) => GraphQLConnection<TItem>,
    private readonly usePageInfoFlags: boolean,
    private readonly createVariables: (paging: ConnectionPagingVariables<TSortField, TBackendField>) => TVariables,
    readonly consistency?: ResolvedGraphQLConnectionConsistency<TItem>,
    private readonly codec?: GraphQLQueryCodec
  ) {}

  /** Execute one explicit Connection page without creating a collection. */
  async executePage(
    target: GraphQLQueryExecutionTarget,
    paging: ConnectionPagingVariables<TSortField, TBackendField>,
    options: GraphQLQueryExecutionOptions = {}
  ) {
    const data = await this.createPageQuery(paging).execute(target, options)
    return {
      data,
      connection: this.selectConnection(data),
    }
  }

  async fetchPage(
    target: GraphQLQueryExecutionTarget,
    request: RemoteCollectionPageRequest & { sorting?: RemoteCollectionSorting<TSortField> }
  ): Promise<RemoteCollectionPageResult<TItem>> {
    const { connection } = await this.executePage(
      target,
      {
        pageSize: request.fetchSize,
        after: request.after ?? null,
        before: request.before ?? null,
        skip: request.skip,
        includeTotalCount: request.includeTotalCount,
        sorting: request.sorting,
      },
      { signal: request.signal }
    )

    const pageInfo = connection.pageInfo ?? undefined
    return {
      items: connection.nodes ?? [],
      totalCount: connection.totalCount ?? undefined,
      pageInfo: {
        startCursor: pageInfo?.startCursor,
        endCursor: pageInfo?.endCursor,
      },
      hasNextPage: this.usePageInfoFlags ? pageInfo?.hasNextPage : undefined,
      hasPreviousPage: this.usePageInfoFlags ? pageInfo?.hasPreviousPage : undefined,
    }
  }

  private createPageQuery(paging: ConnectionPagingVariables<TSortField, TBackendField>) {
    return new GraphQLQueryInstance<TData, TVariables>(
      this.endpoint,
      this.document,
      this.createVariables(paging),
      this.codec
    )
  }
}

export class GraphQLConnectionQueryDefinition<
  TData,
  TItem,
  TParameters,
  TVariables extends object,
  TSortField extends string = string,
  TBackendField extends string = string,
> {
  constructor(
    readonly endpoint: string,
    readonly document: GraphQLDocument<TData, TVariables>,
    private readonly variables: ConnectionVariablesBuilder<TParameters, TVariables, TSortField, TBackendField>,
    readonly selectConnection: (data: TData) => GraphQLConnection<TItem>,
    readonly usePageInfoFlags = false,
    private readonly consistency?: GraphQLConnectionQueryConsistency<TParameters, TItem>,
    readonly sorting: Readonly<Record<TSortField, TBackendField>> = Object.freeze({}) as Readonly<
      Record<TSortField, TBackendField>
    >,
    readonly maxSortFields = 1,
    readonly defaultSorting: RemoteCollectionSorting<TSortField> = Object.freeze([]),
    readonly codec?: GraphQLQueryCodec
  ) {
    if (!Number.isInteger(maxSortFields) || maxSortFields < 1)
      throw new Error("GraphQL connection maxSortFields must be a positive integer.")
    if (defaultSorting.length > maxSortFields)
      throw new Error(`GraphQL connection defaultSorting supports at most ${maxSortFields} fields.`)
    if (new Set(defaultSorting.map((sort) => sort.id)).size !== defaultSorting.length)
      throw new Error("GraphQL connection defaultSorting cannot contain duplicate fields.")
    graphQLOrder(defaultSorting, sorting)
  }

  create(...args: ConnectionQueryCreateArguments<TParameters>) {
    if (args.length === 0) {
      const variables = this.variables as (
        paging: ConnectionMapperPagingVariables<TSortField, TBackendField>
      ) => TVariables
      return new GraphQLConnectionQueryInstance<TData, TItem, TVariables, TSortField, TBackendField>(
        this.endpoint,
        this.document,
        this.selectConnection,
        this.usePageInfoFlags,
        (paging) => variables(this.withOrder(paging)),
        this.resolveConsistency(undefined as TParameters),
        this.codec
      )
    }

    const parameters = snapshotParameters(args[0] as TParameters)
    const variables = this.variables as (
      parameters: Readonly<TParameters>,
      paging: ConnectionMapperPagingVariables<TSortField, TBackendField>
    ) => TVariables

    return new GraphQLConnectionQueryInstance<TData, TItem, TVariables, TSortField, TBackendField>(
      this.endpoint,
      this.document,
      this.selectConnection,
      this.usePageInfoFlags,
      (paging) => variables(parameters as Readonly<TParameters>, this.withOrder(paging)),
      this.resolveConsistency(parameters as TParameters),
      this.codec
    )
  }

  private withOrder(paging: ConnectionPagingVariables<TSortField, TBackendField>) {
    const sorting = paging.sorting?.length ? paging.sorting : this.defaultSorting
    return { ...paging, order: graphQLOrder(sorting, this.sorting) }
  }

  private resolveConsistency(parameters: TParameters): ResolvedGraphQLConnectionConsistency<TItem> | undefined {
    if (this.consistency == null) return undefined
    const dependsOn = this.consistency.dependsOn
    const itemDependsOn = this.consistency.itemDependsOn
    return Object.freeze({
      collection: normalizeDataDependencies(
        typeof dependsOn === "function" ? dependsOn(parameters as Readonly<TParameters>) : dependsOn
      ),
      item:
        itemDependsOn == null ? undefined : (value: Readonly<TItem>) => normalizeDataDependencies(itemDependsOn(value)),
    })
  }
}

export function defineGraphQLConnectionQuery<
  TDocument extends AnyGraphQLDocument,
  TConnection extends GraphQLConnection<unknown>,
  const TSorting extends Readonly<Record<string, string>> = Readonly<Record<never, never>>,
>(
  options: GraphQLConnectionQueryDefinitionOptions<TDocument, TConnection, TSorting>
): GraphQLConnectionQueryDefinition<
  GraphQLDocumentData<TDocument>,
  ConnectionItem<TConnection>,
  void,
  GraphQLDocumentVariables<TDocument>,
  Extract<keyof TSorting, string>,
  TSorting[keyof TSorting]
>
export function defineGraphQLConnectionQuery<
  TDocument extends AnyGraphQLDocument,
  TParameters,
  TConnection extends GraphQLConnection<unknown>,
  const TSorting extends Readonly<Record<string, string>> = Readonly<Record<never, never>>,
>(
  options: MappedGraphQLConnectionQueryDefinitionOptions<TDocument, TParameters, TConnection, TSorting>
): GraphQLConnectionQueryDefinition<
  GraphQLDocumentData<TDocument>,
  ConnectionItem<TConnection>,
  TParameters,
  GraphQLDocumentVariables<TDocument>,
  Extract<keyof TSorting, string>,
  TSorting[keyof TSorting]
>
export function defineGraphQLConnectionQuery(
  options:
    | GraphQLConnectionQueryDefinitionOptions<AnyGraphQLDocument, GraphQLConnection<unknown>>
    | MappedGraphQLConnectionQueryDefinitionOptions<AnyGraphQLDocument, unknown, GraphQLConnection<unknown>>
) {
  return new GraphQLConnectionQueryDefinition(
    options.endpoint,
    options.document,
    options.variables as ConnectionVariablesBuilder<unknown, object>,
    options.selectConnection,
    options.usePageInfoFlags,
    options.consistency as GraphQLConnectionQueryConsistency<unknown, unknown> | undefined,
    Object.freeze({ ...options.sorting }),
    options.maxSortFields,
    Object.freeze((options.defaultSorting ?? []).map((sort) => Object.freeze({ ...sort }))),
    options.codec
  )
}

function snapshotParameters<T>(parameters: T): T {
  if (parameters == null || typeof parameters !== "object") return parameters
  return structuredClone(parameters)
}
